import * as React from 'react';
import { BlockKitProvider } from './context/BlockKitContext';
import BaseBlockRenderer from './BlockRenderer';
import { BlockElement, BlockRendererProps, Replacements } from './types';

// Define BlockBuilder type since it's not directly exported
interface BlockBuilder {
  build: () => any;
  [key: string]: any;
}

// Helper type to ensure proper prop types for React components
type PropsWithChildren<P = unknown> = P & {
  children?: React.ReactNode;
};

// Extend the props to include children but make blocks optional in the root
interface BlockKitRootProps {
  children?: React.ReactNode;
  replacements?: Replacements;
  blocks?: BlockElement | BlockElement[] | BlockBuilder;
  onWizardComplete?: () => void;
  [key: string]: any;
}

// Helper type to ensure proper prop types for BlockRenderer
type BlockRendererOnlyProps = Omit<BlockRendererProps, 'blocks' | 'replacements' | 'onWizardComplete'>;

// Type assertion to handle React 18 compatibility
const BlockKitRootComponent: React.FC<BlockKitRootProps> = ({
  replacements = {},
  children,
  blocks,
  onWizardComplete,
  ...rest
}) => {
  // Process blocks if they are in builder format
  const processedBlocks = React.useMemo(() => {
    if (!blocks) return undefined;
    
    // If blocks have a build method, call it to get the actual blocks
    if (typeof blocks === 'object' && blocks !== null && 'build' in blocks) {
      return blocks.build();
    }
    
    // If it's already an array or single block, return as is
    return blocks;
  }, [blocks]);
  
  // Create props for BaseBlockRenderer if blocks are provided
  const blockRendererProps = React.useMemo(() => {
    if (!processedBlocks) return null;

    return {
      blocks: processedBlocks,
      onWizardComplete,
      replacements,
      ...(rest as BlockRendererOnlyProps)
    } as BlockRendererProps;
  }, [processedBlocks, onWizardComplete, replacements, rest]);
  
  // Use a key to force remount when replacements change
  return (
    <BlockKitProvider 
      key={JSON.stringify(replacements)}
      replacements={replacements}
    >
      {children || (blockRendererProps && (
        <BaseBlockRenderer 
          {...blockRendererProps}
          // Explicitly cast to any to avoid type issues with React 18
          {...({} as any)}
        />
      ))}
    </BlockKitProvider>
  );
};

// Create a properly typed BlockKitRoot component
const BlockKitRoot = BlockKitRootComponent as unknown as React.FC<BlockKitRootProps>;

// Set display name for better debugging
BlockKitRoot.displayName = 'BlockKitRoot';

/**
 * BlockKit is a wrapper around BlockKitRoot that provides a more convenient API
 * for rendering blocks. It automatically includes the BlockKitProvider and handles builder objects.
 */
const BlockKitComponent: React.FC<BlockRendererProps> = (props) => {
  const {
    blocks,
    replacements = {},
    onWizardComplete,
    ...rest
  } = props;

  // Process blocks to handle builder objects
  const processedBlocks = React.useMemo(() => {
    if (!blocks) return undefined;
    
    // If blocks is a builder object, call build() on it
    if (typeof blocks === 'object' && blocks !== null && 'build' in blocks) {
      return blocks.build();
    }
    
    // If it's an array, check each item for builder objects
    if (Array.isArray(blocks)) {
      return blocks.map(block => 
        (typeof block === 'object' && block !== null && 'build' in block) 
          ? block.build() 
          : block
      );
    }
    
    return blocks;
  }, [blocks]);

  return (
    <BlockKitRoot 
      blocks={processedBlocks} 
      replacements={replacements}
      onWizardComplete={onWizardComplete}
      {...rest}
    />
  );
};

// Create a properly typed BlockKit component
const BlockKit = BlockKitComponent as unknown as React.FC<BlockRendererProps>;

// Set display name for better debugging
BlockKit.displayName = 'BlockKit';
BlockKitRoot.displayName = 'BlockKitRoot';

// Export BlockKit as default and BlockKitRoot as named export
export { BlockKit as default, BlockKitRoot };
